import classNames from "classnames";
import * as React from "react";

import { CapabilityOverridesEditor } from "@/shared/components/CapabilityOverridesEditor";
import type {
  BedrockCatalogModel,
  ModelCapabilities,
} from "@/shared/domain/orgs";
import {
  useCreateBedrockModel,
  useUpdateBedrockModel,
} from "@/shared/domain/orgs/hooks";
import {
  Dialog,
  RobotoButton,
  Table,
  TextInput,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import styles from "./AiModels.module.css";
import {
  BREAK_EVEN_RAU_PER_USD,
  bedrockModelDraftError,
  bedrockModelRequest,
  breakEvenPrice,
  draftFromModel,
  draftRateMargin,
  mayUsePromptCaching,
  TOKEN_RATE_KEYS,
  TOKEN_RATE_LABELS,
  tokensText,
  type BedrockModelDraft,
  type TokenRatesDraft,
} from "./bedrockModelForm";
import { VisionSettingsEditor } from "./VisionSettingsEditor";
import {
  sameVisionOverrides,
  visionOverridesFromDraft,
} from "./visionSettingsForm";

/** Each rate group's column heading, and the word its inputs are named by. */
const RATE_GROUPS = {
  cost: { heading: "AWS cost ($ / M tokens)", name: "AWS cost" },
  price: { heading: "Customer price (RAU / M tokens)", name: "customer price" },
} as const;

interface BedrockModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The model to edit; `undefined` adds a new one. */
  model: BedrockCatalogModel | undefined;
}

/**
 * Add a Bedrock catalog model, or edit one. The server names a new entry
 * from its label, so the form never asks for (or shows) an id. A model
 * needs both what AWS charges Roboto (USD per million tokens) and what the
 * customer is charged (RAU per million tokens).
 */
export function BedrockModelDialog({
  open,
  onOpenChange,
  model,
}: BedrockModelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={classNames(styles.dialog, styles.modelDialog)}>
        {open && (
          <BedrockModelForm model={model} onClose={() => onOpenChange(false)} />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function BedrockModelForm({
  model,
  onClose,
}: {
  model: BedrockCatalogModel | undefined;
  onClose: () => void;
}) {
  const isCreate = model === undefined;
  const [draft, setDraft] = React.useState<BedrockModelDraft>(() =>
    draftFromModel(model),
  );
  const createMutation = useCreateBedrockModel();
  const updateMutation = useUpdateBedrockModel();
  const mutation = isCreate ? createMutation : updateMutation;

  const error = bedrockModelDraftError(
    draft,
    model?.capabilities,
    model?.vision_defaults,
  );
  const set = (patch: Partial<BedrockModelDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const handleSave = () => {
    if (error !== null) {
      return;
    }
    const request = bedrockModelRequest(draft, model);
    if (model === undefined) {
      createMutation.mutate(request, { onSuccess: onClose });
    } else {
      updateMutation.mutate(
        { modelId: model.id, request },
        { onSuccess: onClose },
      );
    }
  };

  // The server makes Verify's test call before saving a new model, a changed
  // model id, changed capabilities or vision settings, or a changed output
  // limit, and refuses the save when it fails.
  const verifiesOnSave =
    model === undefined ||
    draft.model_id.trim() !== model.model_id ||
    !sameOverrides(draft.capability_overrides, model.capability_overrides) ||
    !sameVisionOverrides(
      visionOverridesFromDraft(draft.vision),
      model.vision_overrides,
    ) ||
    draft.max_output_tokens.trim() !== tokensText(model.max_output_tokens);

  const field = (
    label: string,
    key: Exclude<
      keyof BedrockModelDraft,
      "capability_overrides" | "cost" | "price" | "vision"
    >,
    props: React.ComponentProps<typeof TextInput> = {},
  ) => (
    <label className={styles.formField}>
      <span className={styles.formLabel}>{label}</span>
      <TextInput
        {...props}
        aria-label={label}
        value={draft[key]}
        onChange={(event) => set({ [key]: event.target.value })}
        disabled={mutation.isPending || props.disabled === true}
      />
    </label>
  );

  // One rate's input in the cost or price column. Its accessible name says
  // which rate and which column, e.g. "Input AWS cost".
  const rateInput = (group: "cost" | "price", key: keyof TokenRatesDraft) => (
    <TextInput
      aria-label={`${TOKEN_RATE_LABELS[key]} ${RATE_GROUPS[group].name}`}
      className={styles.pricingInput}
      inputMode="decimal"
      value={draft[group][key]}
      onChange={(event) =>
        setDraft((prev) => ({
          ...prev,
          [group]: { ...prev[group], [key]: event.target.value },
        }))
      }
      disabled={mutation.isPending}
    />
  );
  const breakEven = breakEvenPrice(draft);
  // Without prompt caching no token is billed at a cache rate, so a blank
  // one saves as 0.
  const cacheOptional = !mayUsePromptCaching(draft, model?.capabilities);

  return (
    <div className={styles.dialogBody}>
      <Dialog.Title>
        {isCreate ? "Add Bedrock model" : `Edit ${model.label}`}
      </Dialog.Title>

      {field("Label", "label", { placeholder: "Shown in the chat picker" })}
      {field("Description", "description")}
      {field("Bedrock model id", "model_id", {
        placeholder: "Model id, inference-profile id or ARN",
      })}

      <p className={styles.note}>
        Customers are charged the price; the cost is what AWS charges Roboto. 1
        RAU = 1 AI credit = ${(1 / BREAK_EVEN_RAU_PER_USD).toFixed(2)}.
      </p>
      <Table
        layout="fixed"
        className={styles.pricingTable}
        aria-label="Pricing per million tokens"
      >
        <colgroup>
          <col className={styles.pricingRateCol} />
          <col />
          <col />
          <col className={styles.pricingMarginCol} />
        </colgroup>
        <Table.Header variant="compact">
          <Table.Row>
            <Table.Head scope="col" className={styles.pricingCell}>
              <span className="sr-only">Rate</span>
            </Table.Head>
            <Table.Head scope="col" className={styles.pricingCell}>
              {RATE_GROUPS.cost.heading}
            </Table.Head>
            <Table.Head scope="col" className={styles.pricingCell}>
              <span className={styles.pricingPriceHead}>
                {RATE_GROUPS.price.heading}
                <RobotoButton
                  variant="secondary"
                  size="sm"
                  disabled={breakEven === null || mutation.isPending}
                  title={
                    breakEven === null
                      ? "Set the input and output cost first."
                      : `Set each price to its cost × ${BREAK_EVEN_RAU_PER_USD} RAU.`
                  }
                  onClick={() => {
                    if (breakEven !== null) {
                      set({ price: breakEven });
                    }
                  }}
                >
                  Match cost (break-even)
                </RobotoButton>
              </span>
            </Table.Head>
            <Table.Head scope="col" className={styles.pricingCell}>
              Margin
            </Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {TOKEN_RATE_KEYS.map((key) => {
            const margin = draftRateMargin(draft, key);
            const isCache = key === "cacheRead" || key === "cacheWrite";
            return (
              <Table.Row key={key}>
                <Table.Head scope="row" className={styles.pricingCell}>
                  <span className={styles.pricingRateLabel}>
                    {TOKEN_RATE_LABELS[key]}
                    {isCache && cacheOptional && (
                      <span className={styles.pricingOptional}>optional</span>
                    )}
                  </span>
                </Table.Head>
                <Table.Cell className={styles.pricingCell}>
                  {rateInput("cost", key)}
                </Table.Cell>
                <Table.Cell className={styles.pricingCell}>
                  {rateInput("price", key)}
                </Table.Cell>
                <Table.Cell
                  className={classNames(
                    styles.pricingCell,
                    styles.pricingMargin,
                  )}
                >
                  {margin ?? "—"}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      <CapabilityOverridesEditor
        value={draft.capability_overrides}
        onChange={(capability_overrides) => set({ capability_overrides })}
        effective={model?.capabilities}
        // A new model's family is detected once it is saved.
        family={model === undefined ? undefined : model.model_family}
        disabled={mutation.isPending}
      />
      <VisionSettingsEditor
        value={draft.vision}
        onChange={(vision) => set({ vision })}
        defaults={model?.vision_defaults}
        disabled={mutation.isPending}
      />
      {field("Context window (tokens)", "context_window_tokens", {
        inputMode: "numeric",
        placeholder: "Deployment default",
      })}
      {field("Max output tokens", "max_output_tokens", {
        inputMode: "numeric",
        placeholder: "No limit",
      })}

      {verifiesOnSave && (
        <p className={styles.note}>
          {isCreate
            ? "Adding the model makes a test call to it; a model that fails the call is not added."
            : "Saving makes a test call with the new model id, capabilities or vision settings; a change that fails the call is not saved."}
        </p>
      )}
      {mutation.isPending && verifiesOnSave && (
        <p className={styles.note} role="status">
          Verifying {isCreate ? draft.label.trim() : model.label} with a test
          call…
        </p>
      )}
      {error !== null && <p className={styles.note}>{error}</p>}
      {mutation.isError && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(mutation.error)}
        </p>
      )}

      <div className={styles.dialogActions}>
        <RobotoButton
          variant="ghost"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          onClick={handleSave}
          disabled={error !== null}
          loading={mutation.isPending}
        >
          {isCreate ? "Add model" : "Save"}
        </RobotoButton>
      </div>
    </div>
  );
}

/** Whether two sets of capability overrides set the same flags to the same values. */
function sameOverrides(
  a: Partial<ModelCapabilities>,
  b: Partial<ModelCapabilities> | undefined,
): boolean {
  const other = b ?? {};
  const keys = Object.keys(a) as (keyof ModelCapabilities)[];
  return (
    keys.length === Object.keys(other).length &&
    keys.every((key) => a[key] === other[key])
  );
}
